/*
 * File: GetCoursePeriodEnrollments.ts
 * Author: Austin Robinson
 * Date Created: 8/28/2023
 * Copyright (c) DrivingDoc 2023
 */
import { AxiosError } from 'axios';
import { createQuery } from 'react-query-kit';

import { ErrorResponse, ModifiedByUser, PostGreSQLInterval, privateApi } from 'api';

export interface GetCoursePeriodEnrollmentsParams {
  id: string;
}

export interface CoursePeriodEnrollment {
  tenant_id: string;
  student_user_id: string;
  course_period_id: string;
  course_enrollment_id: string;
  attendance: 'present' | 'absent' | 'no_permit' | null;
  attendance_taken_at: string | null;
  absence_fee: string | null;
  no_permit_fee: string | null;
  canceled_at: string | null;
  late_cancel_override: boolean;
  cancellation_fee: string | null;
  late_cancellation_fee: string | null;
  notes: string | null;
  private_notes: string | null;
  created_by: string | null;
  created_at: string | null;
  performance_time_earned: PostGreSQLInterval | null;
  observation_time_earned: PostGreSQLInterval | null;
  enrollment_type: 'performance_and_observation' | 'observation_only';
  period_number: number;

  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  nickname: string | null;
  attendance_taken_by_user: ModifiedByUser | null;
  canceled_by_user: ModifiedByUser | null;
  created_by_user: ModifiedByUser | null;
}

export type GetCoursePeriodEnrollmentsResult = CoursePeriodEnrollment[];

async function getCoursePeriodEnrollments(params: GetCoursePeriodEnrollmentsParams) {
  // Set the API endpoint URL
  const result = await privateApi.get<GetCoursePeriodEnrollmentsResult>(
    `${import.meta.env.VITE_API_BASE_URL}/courses/periods/${params?.id}/enrollments`,
  );

  // return the data object
  return result.data;
}

const useGetCoursePeriodEnrollments = createQuery<
  GetCoursePeriodEnrollmentsResult,
  GetCoursePeriodEnrollmentsParams,
  AxiosError<ErrorResponse>
>({
  queryKey: ['course_period_enrollments'],
  fetcher: (variables) => getCoursePeriodEnrollments(variables),
});

export { getCoursePeriodEnrollments, useGetCoursePeriodEnrollments };
